import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Select, 
  DatePicker, 
  message, 
  Tag, 
  Space,
  Card,
  Modal,
  Descriptions,
  Dropdown
} from 'antd';
import { 
  ReloadOutlined,
  EyeOutlined,
  DownOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;
const { RangePicker } = DatePicker;

const OperationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDevice, setFilterDevice] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [logsRes, devicesRes] = await Promise.all([
        api.get('/logs', {
          params: {
            deviceId: filterDevice,
            type: filterType,
            startDate: dateRange ? dateRange[0].format('YYYY-MM-DD HH:mm:ss') : null,
            endDate: dateRange ? dateRange[1].format('YYYY-MM-DD HH:mm:ss') : null,
            limit: 100
          }
        }),
        api.get('/devices')
      ]);

      setLogs(logsRes.data.data || []);
      setDevices(devicesRes.data.data || []);
    } catch (error) {
      message.error('获取日志失败');
      console.error('Fetch logs error:', error);
    } finally {
      setLoading(false);
    }
  }, [filterDevice, filterType, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showDetail = (log) => {
    setSelectedLog(log);
    setDetailVisible(true);
  };

  const getTypeColor = (type) => {
    const colorMap = {
      device_register: 'blue',
      device_update: 'cyan',
      device_delete: 'red',
      apk_upload: 'green',
      apk_delete: 'orange',
      command_create: 'purple',
      command_install: 'blue',
      command_uninstall: 'orange',
      command_update: 'cyan',
      command_cancel: 'gray'
    };
    return colorMap[type] || 'default';
  };

  const getTypeLabel = (type) => {
    const labelMap = {
      device_register: '设备注册',
      device_update: '设备更新',
      device_delete: '设备删除',
      apk_upload: 'APK上传',
      apk_delete: 'APK删除',
      command_create: '创建指令',
      command_install: '安装指令',
      command_uninstall: '卸载指令',
      command_update: '指令更新',
      command_cancel: '取消指令'
    };
    return labelMap[type] || type;
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (date) => new Date(date).toLocaleString('zh-CN')
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => (
        <Tag color={getTypeColor(type)}>
          {getTypeLabel(type)}
        </Tag>
      )
    },
    {
      title: '设备名称',
      dataIndex: 'deviceId',
      key: 'deviceId',
      width: 150,
      render: (deviceId) => {
        const device = devices.find(d => d.deviceId === deviceId);
        return device ? device.deviceName : deviceId;
      }
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'detail',
                icon: <EyeOutlined />,
                label: '详情',
                onClick: () => showDetail(record)
              }
            ]
          }}
          trigger={['hover']}
        >
          <Button>
            操作 <DownOutlined />
          </Button>
        </Dropdown>
      )
    }
  ];

  return (
    <div>
      <Card
        title="操作日志"
        extra={
          <Space>
            <Select
              placeholder="选择设备"
              style={{ width: 200 }}
              allowClear
              onChange={setFilterDevice}
              value={filterDevice}
            >
              {devices.map(device => (
                <Option key={device.deviceId} value={device.deviceId}>
                  {device.deviceName}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="选择类型"
              style={{ width: 150 }}
              allowClear
              onChange={setFilterType}
              value={filterType}
            >
              <Option value="device_register">设备注册</Option>
              <Option value="device_update">设备更新</Option>
              <Option value="device_delete">设备删除</Option>
              <Option value="apk_upload">APK上传</Option>
              <Option value="apk_delete">APK删除</Option>
              <Option value="command_create">创建指令</Option>
              <Option value="command_install">安装指令</Option>
              <Option value="command_uninstall">卸载指令</Option>
              <Option value="command_update">指令更新</Option>
              <Option value="command_cancel">取消指令</Option>
            </Select>
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              onChange={setDateRange}
              value={dateRange}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={logs}
          columns={columns}
          rowKey={(record) => `${record.timestamp}-${record.type}-${record.message}`}
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条日志`
          }}
        />
      </Card>

      <Modal
        title="日志详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedLog && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="时间" span={2}>
              {new Date(selectedLog.timestamp).toLocaleString('zh-CN')}
            </Descriptions.Item>
            <Descriptions.Item label="类型">
              <Tag color={getTypeColor(selectedLog.type)}>
                {getTypeLabel(selectedLog.type)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="设备ID">
              {selectedLog.deviceId || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="消息" span={2}>
              {selectedLog.message}
            </Descriptions.Item>
            <Descriptions.Item label="详细信息" span={2}>
              <pre style={{ 
                background: '#f5f5f5', 
                padding: '8px', 
                borderRadius: '4px',
                margin: 0,
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                {JSON.stringify(selectedLog.details || {}, null, 2)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default OperationLogs;
